import { Injectable, inject, untracked } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { SettingsService } from './settings.service';

/**
 * Datepicker adapter that reads its locale from SettingsService rather than
 * being handed one through `setLocale()`.
 *
 * Locale is purely a display concern, but `DateAdapter.setLocale()` broadcasts
 * on `localeChanges`, which makes every date input re-assign its current value.
 * On a `mat-date-range-input` that re-assignment refreshes the sibling input's
 * validator, which calls `updateValueAndValidity()` and emits `valueChanges` on
 * the range form group — indistinguishable from the user picking a new range,
 * so it reloaded the report. Reading the locale on demand keeps formatting
 * correct without that round-trip through the forms layer.
 *
 * Formatting also stays independent of the reporting time zone. The picker
 * holds calendar dates (local midnight, matching the YYYY-MM-DD the API is
 * given), and NativeDateAdapter renders their local Y/M/D parts verbatim.
 * Applying `SettingsService.timezone` here would shift the displayed day away
 * from the day actually queried.
 */
@Injectable()
export class PortalDateAdapter extends NativeDateAdapter {
  private readonly settings = inject(SettingsService);

  constructor() {
    super();
    // `locale` is a plain field on DateAdapter, so an accessor declared on this
    // subclass would be shadowed by that field. Define it on the instance
    // instead, once super() has installed the field.
    Object.defineProperty(this, 'locale', {
      get: () => untracked(this.settings.locale),
      set: () => {}, // Material's setLocale() writes here — deliberately ignored
      configurable: true
    });
  }

  /** No-op — see the class docs for why the locale is never pushed in. */
  override setLocale(): void {}
}
